import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AddAccountRequest,
  AddHubspotCompanyRequest,
  AddHubspotCompanyResponse,
  AddAccountResponse,
  UpdateAccountRequest,
  UpdateAccountResponse,
  addAccountContract,
  addHubspotCompanyContract,
  updateAccountContract
} from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { accountKeys } from '@/pages/admin/api/queries/useAccounts';

/**
 * Assign an account its company, or nest it under a parent.
 *
 * Both changes move a row in the account list — a company gives it its name, a
 * parent moves it into a group — so the list is invalidated either way.
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountId,
      data
    }: {
      accountId: string;
      data: UpdateAccountRequest;
    }): Promise<UpdateAccountResponse> => {
      const { data: response } = await apiClient.patch<UpdateAccountResponse>(
        updateAccountContract.path.replace(':accountId', accountId),
        data
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.all });
    }
  });
}

/** Creates a customer. The company is optional — a customer can precede it. */
export function useAddAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AddAccountRequest): Promise<AddAccountResponse> => {
      const { data: response } = await apiClient.post<AddAccountResponse>(
        addAccountContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.all });
    }
  });
}

/**
 * Creates the CRM record for a customer that has none yet.
 *
 * Separate from creating the account: the remote write is the step that can
 * half-succeed, so it happens first and alone. If the account create then
 * fails, the company exists and the picker finds it.
 */
export function useAddHubspotCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AddHubspotCompanyRequest): Promise<AddHubspotCompanyResponse> => {
      const { data: response } = await apiClient.post<AddHubspotCompanyResponse>(
        addHubspotCompanyContract.path,
        data
      );
      return response;
    },
    onSuccess: () => {
      // The new company must show up in the next search, which is keyed by term.
      void queryClient.invalidateQueries({ queryKey: accountKeys.all });
    }
  });
}
